import { useNavigate } from "react-router-dom";
import subscriptionApi from "../../api/subscription";
import { useEffect, useState } from "react";
import SectionCard from "./AccounSettingSectionCard";
import {
  AlertCircle,
  Check,
  CreditCard,
  Crown,
  XIcon,
  RefreshCw,
  Settings,
  Ban,
  CheckCircle,
  Send,
} from "lucide-react";
import Button from "../common/Button";
import InfoRow from "./InfoRow";
import { toast } from "react-toastify";
import AlertDialogSlide from "../common/AlertDialog";
import PlanCard from "./PlanCard";
import { useQuery } from "@tanstack/react-query";
import useSubscriptionLimits from "../../hooks/useSubscriptionLimits";

const BillingAndSubscriptions = () => {
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [actionLoading, setActionLoading] = useState({
    cancel: false,
    autoRenew: false,
    paymentMethod: false,
    plan: false,
  });
  const navigate = useNavigate();

  const {
    data: subscriptionLimits,
    error: subscriptionLimitsError,
    isError: isSubscriptionLimitsError,
    isLoading: isSubscriptionLimitsLoading,
    refetch: refetchSubscriptionLimits,
  } = useSubscriptionLimits();

  const {
    data: subscriptionPlans,
    isLoading,
    isError,
    error: subscriptionsError,
    refetch,
  } = useQuery({
    queryKey: ["subscriptions"],
    queryFn: async () => {
      const res = await subscriptionApi.getSubcriptionPlans();
      return res.data;
    },
  });

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  // Initialize selected payment method when subscription data loads
  useEffect(() => {
    if (subscriptionData?.subscription?.payment_method) {
      setSelectedPaymentMethod(subscriptionData.subscription.payment_method);
    }
  }, [subscriptionData]);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      const response = await subscriptionApi.getMySubscription();
      setSubscriptionData(response.data);
    } catch (err) {
      setError("Failed to load subscription data");
      console.error("Subscription fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setActionLoading((prev) => ({ ...prev, cancel: true }));
    try {
      const res = await subscriptionApi.cancelSubscription();
      await fetchSubscriptionData();
      if (res.status === 200) {
        toast.success(
          "Subscription cancelled successfully. You'll retain access until the end of your billing period."
        );
      }
    } catch (err) {
      toast.error("Failed to cancel subscription. Please try again.");
      console.error("Cancel subscription error:", err);
    } finally {
      setActionLoading((prev) => ({ ...prev, cancel: false }));
      setCancelDialogOpen(false);
    }
  };

  const handleToggleAutoRenew = async () => {
    const newAutoRenewValue = !subscriptionData.subscription.auto_renew;

    setActionLoading((prev) => ({ ...prev, autoRenew: true }));
    try {
      await subscriptionApi.updateAutoRenew(newAutoRenewValue);
      setSubscriptionData((prev) => ({
        ...prev,
        subscription: {
          ...prev.subscription,
          auto_renew: newAutoRenewValue,
        },
      }));
      toast.success(
        `Auto-renewal ${
          newAutoRenewValue ? "enabled" : "disabled"
        } successfully!`
      );
    } catch (err) {
      if (err.response && err.response.data && err.response.data.error) {
        return toast.error(err.response.data.error);
      }
      toast.error("Failed to update auto-renewal settings. Please try again.");
      console.error("Auto-renew update error:", err);
    } finally {
      setActionLoading((prev) => ({ ...prev, autoRenew: false }));
    }
  };

  const handlePaymentMethodSelect = (methodId) => {
    setSelectedPaymentMethod(methodId);
  };

  const handleUpdatePaymentMethod = async () => {
    if (!selectedPaymentMethod) {
      toast.info("Please select a payment method first.");
      return;
    }

    if (
      selectedPaymentMethod === subscriptionData.subscription.payment_method
    ) {
      toast.info("This payment method is already active.");
      return;
    }

    setActionLoading((prev) => ({ ...prev, paymentMethod: true }));
    try {
      await subscriptionApi.updatePaymentMethod(selectedPaymentMethod);
      await fetchSubscriptionData();
      toast.success("Payment method updated successfully!");
    } catch (err) {
      if (err.response && err.response.data && err.response.data.error) {
        return toast.error(err.response.data.error);
      }
      toast.error("Failed to update payment method. Please try again.");
      console.error("Payment method update error:", err);
    } finally {
      setActionLoading((prev) => ({ ...prev, paymentMethod: false }));
    }
  };

  const handleUpdatePlan = async (planId) => {
    // if (!window.confirm("Are you sure you want to change your subscription plan? This may affect your billing immediately.")) {
    //   return;
    // }

    setActionLoading((prev) => ({ ...prev, plan: true }));
    try {
      await subscriptionApi.updateSubscriptionPlan(planId);
      await fetchSubscriptionData();
      toast.success("Subscription plan updated successfully!");
    } catch (err) {
      toast.error("Failed to update subscription plan. Please try again.");
      console.error("Plan update error:", err);
    } finally {
      setActionLoading((prev) => ({ ...prev, plan: false }));
    }
  };

  // Convert features object to array for PlanCard component
  const formatFeatures = (featuresObj) => {
    if (!featuresObj) return [];

    return Object.entries(featuresObj)
      .map(([key, value]) => {
        // Convert key to readable format and handle boolean values
        const featureName = key
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        if (typeof value === "boolean") {
          return value ? featureName : null;
        } else if (typeof value === "number") {
          return `${value} ${featureName}`;
        } else {
          return featureName;
        }
      })
      .filter(Boolean); // Remove null values
  };

  // Payment method options
  const paymentMethods = [
    { id: "daraja", name: "M-Pesa", icon: "📱" },
    { id: "stripe", name: "Credit/Debit Card", icon: "💳" },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <SectionCard
          title="Billing & Subscriptions"
          subtitle="Manage your subscription plans and payment history"
        >
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        </SectionCard>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <SectionCard title="Billing & Subscriptions">
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={fetchSubscriptionData} variant="outline">
              Try Again
            </Button>
          </div>
        </SectionCard>
      </div>
    );
  }

  const hasActiveSubscription =
    subscriptionData?.has_subscription 
    // &&
    // subscriptionData.subscription?.plan.is_active;

  const isPaymentMethodChanged =
    selectedPaymentMethod !== subscriptionData.subscription?.payment_method;
  const currentPaymentMethod = subscriptionData.subscription?.payment_method;

  return (
    <div className="space-y-6">
      {/* Current Subscription Status */}
      <SectionCard
        title="Current Subscription"
        subtitle="Your active plan and billing information"
      >
        {hasActiveSubscription ? (
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-primary-50 to-primary-100 rounded-xl border border-primary-200">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center">
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-lg text-primary-900">
                    {subscriptionData.subscription.plan.name}
                  </h4>
                  <p className="text-primary-700">
                    ${subscriptionData.subscription.plan.price}{" "}
                    {subscriptionData.subscription.plan.currency} •{" "}
                    {currentPaymentMethod === "daraja" ? "Mpesa" : "Card"}
                  </p>
                </div>
              </div>
              <div
                className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  subscriptionData.subscription.status === "active"
                    ? "bg-green-100 text-green-800"
                    : "bg-amber-100 text-amber-800"
                }`}
              >
                {subscriptionData.subscription.status.charAt(0).toUpperCase() +
                  subscriptionData.subscription.status.slice(1)}
              </div>
            </div>

            {/* Subscription Details */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h5 className="font-semibold text-secondary-800">
                  Subscription Details
                </h5>
                <InfoRow label="Billing Cycle" value="Monthly" />
                <InfoRow
                  label="Started"
                  value={new Date(
                    subscriptionData.subscription.start_date
                  ).toLocaleDateString("en-US", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                />
                <InfoRow
                  label="Renews"
                  value={new Date(
                    subscriptionData.subscription.end_date
                  ).toLocaleDateString("en-US", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                />

                {/* Auto Renew with Toggle */}
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm font-medium text-secondary-600">
                    Auto Renew
                  </span>
                  <div className="flex items-center gap-3">
                    <span
                      className={`text-sm font-semibold ${
                        subscriptionData.subscription.auto_renew
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {subscriptionData.subscription.auto_renew
                        ? "Enabled"
                        : "Disabled"}
                    </span>
                    <button
                      onClick={handleToggleAutoRenew}
                      disabled={actionLoading.autoRenew}
                      className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors ${
                        subscriptionData.subscription.auto_renew
                          ? "bg-green-500"
                          : "bg-gray-300"
                      } ${
                        actionLoading.autoRenew
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer"
                      }`}
                    >
                      <div
                        className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform ${
                          subscriptionData.subscription.auto_renew
                            ? "translate-x-6"
                            : ""
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h5 className="font-semibold text-secondary-800">
                  Plan Features
                </h5>
                {subscriptionData.subscription.plan.features &&
                  Object.entries(
                    subscriptionData.subscription.plan.features
                  ).map(([key, value]) => (
                    <div key={key} className="flex items-center space-x-3">
                      {value ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <XIcon className="w-4 h-4 text-red-500" />
                      )}
                      <span className="text-secondary-700 capitalize">
                        {key.replace(/_/g, " ")}
                      </span>
                    </div>
                  ))}
              </div>
            </div>

            {/* Usage Limits Section */}
            {!isSubscriptionLimitsLoading && subscriptionLimits && (
              <div className="pt-4 border-t border-gray-100">
                <h5 className="font-semibold text-secondary-800 mb-4">
                  Usage Limits
                </h5>
                <div className="grid gap-3">
                  {/* Job Applications Usage */}
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                        <Send className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <span className="text-sm font-medium text-blue-800">
                          Job Applications
                        </span>
                        <div className="text-xs text-blue-600">
                          {subscriptionLimits.limits.job_applications
                            .remaining === -1
                            ? "Unlimited applications available"
                            : `${subscriptionLimits.limits.job_applications.remaining} remaining this period`}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-semibold text-blue-900">
                        {
                          subscriptionLimits.limits.job_applications.current_count
                        }
                        {subscriptionLimits.limits.job_applications.limit > 0
                          ? ` / ${subscriptionLimits.limits.job_applications.limit}`
                          : " used"}
                      </span>
                      <div
                        className={`text-xs font-medium ${
                          subscriptionLimits.limits.job_applications.can_apply
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {subscriptionLimits.limits.job_applications.can_apply
                          ? "Active"
                          : "Limit Reached"}
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar for Limited Plans */}
                  {subscriptionLimits.limits.job_applications.limit > 0 && (
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${Math.min(
                            (subscriptionLimits.limits.job_applications.current_count /
                              subscriptionLimits.limits.job_applications.limit) *
                              100,
                            100
                          )}%`,
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Reset Information */}
                {subscriptionLimits.limits.job_applications.reset_date && (
                  <div className="mt-2 text-xs text-gray-500 text-center">
                    Resets on{" "}
                    {new Date(
                      subscriptionLimits.limits.job_applications.reset_date
                    ).toLocaleDateString("en-US", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                  </div>
                )}
              </div>
            )}

            {/* Loading state for Usage Limits */}
            {isSubscriptionLimitsLoading && (
              <div className="pt-4 border-t border-gray-100">
                <h5 className="font-semibold text-secondary-800 mb-4">
                  Usage Limits
                </h5>
                <div className="animate-pulse">
                  <div className="h-16 bg-gray-200 rounded-lg"></div>
                </div>
              </div>
            )}

            {/* Error state for Usage Limits */}
            {isSubscriptionLimitsError && (
              <div className="pt-4 border-t border-gray-100">
                <h5 className="font-semibold text-secondary-800 mb-4">
                  Usage Limits
                </h5>
                <div className="p-3 bg-red-50 rounded-lg border border-red-200 text-center">
                  <AlertCircle className="w-5 h-5 text-red-500 mx-auto mb-2" />
                  <p className="text-red-600 text-sm">
                    Failed to load usage data
                  </p>
                  <Button
                    onClick={refetchSubscriptionLimits}
                    variant="outline"
                    size="sm"
                    className="mt-2"
                  >
                    Retry
                  </Button>
                </div>
              </div>
            )}

            {/* Payment Method Selection */}
            <div className="pt-4 border-t border-gray-100">
              <div className="flex justify-between items-center mb-4">
                <h5 className="font-semibold text-secondary-800">
                  Payment Method
                </h5>
                {isPaymentMethodChanged && (
                  <span className="text-sm text-amber-600 bg-amber-50 px-2 py-1 rounded">
                    Changes pending
                  </span>
                )}
              </div>
              <div className="flex gap-3 flex-wrap mb-4">
                {paymentMethods.map((method) => (
                  <button
                    key={method.id}
                    onClick={() => handlePaymentMethodSelect(method.id)}
                    disabled={actionLoading.paymentMethod}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                      selectedPaymentMethod === method.id
                        ? "border-primary-500 bg-primary-50 text-primary-700"
                        : "border-gray-200 hover:border-primary-300 text-secondary-700"
                    } ${
                      actionLoading.paymentMethod
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    }`}
                  >
                    <span>{method.icon}</span>
                    <span>{method.name}</span>
                    {currentPaymentMethod === method.id && (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    )}
                    {selectedPaymentMethod === method.id &&
                      currentPaymentMethod !== method.id && (
                        <div className="w-4 h-4 rounded-full bg-primary-500 flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                  </button>
                ))}
              </div>

              {/* Update Payment Button */}
              {isPaymentMethodChanged && (
                <div className="flex gap-3 items-center">
                  <Button
                    onClick={handleUpdatePaymentMethod}
                    loading={actionLoading.paymentMethod}
                    icon={<CreditCard className="w-4 h-4" />}
                  >
                    Update to{" "}
                    {
                      paymentMethods.find((m) => m.id === selectedPaymentMethod)
                        ?.name
                    }
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() =>
                      setSelectedPaymentMethod(currentPaymentMethod)
                    }
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-4 pt-4 border-t border-gray-100">
              {/* <Button 
                variant="outline"
                onClick={() => navigate('/pricing')}
                icon={<RefreshCw className="w-4 h-4" />}
              >
                Change Plan
              </Button> */}

              <AlertDialogSlide
                title="Confirm Cancellation"
                description="Are you sure you want to cancel your subscription? You'll lose access to premium features at the end of your billing period."
                open={cancelDialogOpen}
                setOpen={setCancelDialogOpen}
                onConfirm={handleCancelSubscription}
              >
                <Button
                  variant="outline"
                  onClick={() => setCancelDialogOpen(true)}
                  icon={<Ban className="w-4 h-4" />}
                  loading={actionLoading.cancel}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  Cancel Subscription
                </Button>
              </AlertDialogSlide>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Crown className="w-8 h-8 text-secondary-400" />
            </div>
            <h4 className="text-lg font-semibold text-secondary-800 mb-2">
              No Active Subscription
            </h4>
            <p className="text-secondary-600 mb-6 max-w-md mx-auto">
              You're currently on the free plan. Upgrade to unlock premium
              features and enhanced capabilities.
            </p>
            <Button
              variant="primary"
              size="lg"
              onClick={() => navigate("/pricing")}
            >
              Browse Plans
            </Button>
          </div>
        )}
      </SectionCard>

      {/* Available Plans */}
      {isLoading ? (
        <SectionCard
          title="Available Plans"
          subtitle="Choose the plan that fits your needs"
        >
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        </SectionCard>
      ) : isError ? (
        <SectionCard
          title="Available Plans"
          subtitle="Choose the plan that fits your needs"
        >
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 mb-4">
              {subscriptionsError?.message || "Failed to load plans"}
            </p>
            <Button onClick={refetch} variant="outline">
              Try Again
            </Button>
          </div>
        </SectionCard>
      ) :
      hasActiveSubscription ?
       (
        <SectionCard
          title="Available Plans"
          subtitle="Choose the plan that fits your needs"
        >
          <div className="grid md:grid-cols-3 gap-6">
            {subscriptionPlans?.map((plan) => (
              <PlanCard
                key={plan.id}
                name={plan.name}
                description={plan.description}
                features={formatFeatures(plan.features)}
                price={plan.price}
                current={
                  hasActiveSubscription &&
                  subscriptionData.subscription.plan.id === plan.id
                }
                onConfirm={() => handleUpdatePlan(plan.id)}
              />
            ))}
          </div>
        </SectionCard>
      )
      :
      null
    }
    

      {/* Billing History */}
      {/* <SectionCard
        title="Billing History"
        subtitle="Your recent payments and invoices"
      >
        <div className="text-center py-8">
          <CreditCard className="w-12 h-12 text-secondary-400 mx-auto mb-4" />
          <p className="text-secondary-600">
            No billing history available yet.
          </p>
          <p className="text-secondary-500 text-sm mt-2">
            Your payment history will appear here once you make your first payment.
          </p>
        </div>
      </SectionCard> */}
    </div>
  );
};

export default BillingAndSubscriptions;
